import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Typography,
  LinearProgress
} from '@mui/material';
import { useTranslate } from 'react-admin';
import BaseWidget from './base';
import { DashboardWidget, GaugeWidgetConfig } from '../../../types/dashboard.ui';
import { dashboardAPI } from '../../../api/dashboard';

interface GaugeWidgetProps {
  widget: DashboardWidget;
  editMode?: boolean;
  onEdit?: (widget: DashboardWidget) => void;
  onDelete?: (widget: DashboardWidget) => void;
}

const GaugeWidget: React.FC<GaugeWidgetProps> = ({ widget, editMode, onEdit, onDelete }: GaugeWidgetProps) => {
  const translate = useTranslate();
  const [value, setValue] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const config = useMemo(() => 
    (typeof widget.config === 'string' ? JSON.parse(widget.config) : widget.config) as GaugeWidgetConfig || {}, 
    [widget.config]
  );

  // Извлекаем источник данных из конфигурации
  const dataSource = useMemo(() => {
    if (config.dataSource) {
      // Новый формат с deviceId, archiveTypeId, paramId
      return config.dataSource;
    }
    // Старый формат - для обратной совместимости
    if (config.paramId || config.param_id) {
      return {
        deviceId: config.deviceId || '',
        archiveTypeId: config.archiveTypeId || '',
        paramId: config.paramId || config.param_id || ''
      };
    }
    return null;
  }, [config]);

  const loadGaugeData = useCallback(() => {
    setLoading(true);
    setError('');

    if (!dataSource || !dataSource.paramId) {
      setError(translate('resources.dashboards.widgets.gauge.errors.no_param'));
      setLoading(false);
      return;
    }

    // Получаем текущее значение параметра через API исторических данных
    // Запрашиваем последнее значение за последний час
    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - 60 * 60 * 1000); // 1 час назад

    dashboardAPI.getHistoricalDataByParams(
      [dataSource.paramId],
      startTime.toISOString(),
      endTime.toISOString()
    )
      .then((data) => {
        if (data && data.length > 0 && data[0].values && data[0].values.length > 0) {
          // Берем последнее значение
          const lastValue = data[0].values[data[0].values.length - 1];
          const numericValue = parseFloat(String(lastValue.value));
          setValue(isNaN(numericValue) ? 0 : numericValue);
        } else {
          // Если нет данных, показываем 0
          setValue(0);
        }
      })
      .catch((err) => {
        console.error('Error loading gauge data:', err);
        setError(translate('resources.dashboards.widgets.gauge.errors.load_failed'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataSource, translate]);

  const formatValue = (val: number): string => {
    if (config.format) {
      try {
        return config.format.replace('{value}', val.toFixed(1));
      } catch {
        return val.toFixed(1);
      }
    }
    return val.toFixed(1);
  };

  const getProgressColor = (val: number): "blue" | "red" | "yellow" | "green" => {
    if (!config.thresholds || !Array.isArray(config.thresholds)) return 'blue';
    
    // Сортируем пороги по убыванию значения
    const sortedThresholds = [...config.thresholds].sort((a, b) => b.value - a.value);
    
    // Находим первый порог, который превышает текущее значение
    for (const threshold of sortedThresholds) {
      if (val >= threshold.value) {
        // Преобразуем цвет hex в допустимое значение LinearProgress
        const color = threshold.color.toLowerCase();
        if (color.includes('f44') || color.includes('red')) return 'red';
        if (color.includes('ff9') || color.includes('ffc') || color.includes('yellow') || color.includes('orange')) return 'yellow';
        return 'blue';
      }
    }
    return 'green';
  };

  const getProgressPercentage = (val: number): number => {
    const minVal = config.minValue ?? config.min_value ?? 0;
    const maxVal = config.maxValue ?? config.max_value ?? 100;
    const range = maxVal - minVal;
    const normalizedValue = Math.max(0, Math.min(val - minVal, range));
    return (normalizedValue / range) * 100;
  };

  const getStatusText = (val: number): string => {
    if (!config.thresholds || !Array.isArray(config.thresholds)) {
      return translate('resources.dashboards.widgets.gauge.status.normal');
    }
    
    // Сортируем пороги по убыванию значения
    const sortedThresholds = [...config.thresholds].sort((a, b) => b.value - a.value);
    
    // Находим первый порог, который превышает текущее значение
    for (const threshold of sortedThresholds) {
      if (val >= threshold.value) {
        return threshold.label || translate('resources.dashboards.widgets.gauge.status.threshold');
      }
    }
    return translate('resources.dashboards.widgets.gauge.status.normal');
  };

  useEffect(() => {
    loadGaugeData();
    
    // Set up auto refresh if configured
    let interval: NodeJS.Timeout;
    const refreshInterval = config.refreshInterval || 5000; // По умолчанию 5 секунд
    if (refreshInterval > 0) {
      interval = setInterval(loadGaugeData, refreshInterval);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [dataSource, config.refreshInterval, loadGaugeData]);

  // Получаем информацию об источнике данных для отображения
  const dataSourceInfo = useMemo(() => {
    if (dataSource) {
      const parts: string[] = [];
      
      // Извлекаем имена из dataSource, если они есть
      if ('deviceObjectName' in dataSource && dataSource.deviceObjectName) {
        parts.push(dataSource.deviceObjectName);
      }
      if ('deviceName' in dataSource && dataSource.deviceName) {
        parts.push(dataSource.deviceName);
      }
      if ('paramName' in dataSource && dataSource.paramName) {
        parts.push(dataSource.paramName);
      }
      
      if (parts.length > 0) {
        return parts.join(' → ');
      }
    }
    return null;
  }, [dataSource]);

  return (
    <BaseWidget
      widget={widget}
      editMode={editMode}
      onEdit={() => onEdit?.(widget)}
      onDelete={() => onDelete?.(widget)}
      onRefresh={loadGaugeData}
      loading={loading}
      error={error}
    >
      <div className="h-full flex flex-col items-center justify-center p-4">
        {/* Data Source Info - только в режиме редактирования */}
        {editMode && dataSourceInfo && (
          <div className="text-center mb-3">
            <Typography variant="caption" color="text.secondary">
              {dataSourceInfo}
            </Typography>
          </div>
        )}

        {/* Current Value */}
        <div className="text-center mb-6">
          <Typography variant="h2" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
            {formatValue(value)}
          </Typography>
          {config.unit && (
            <Typography variant="h6" sx={{ color: 'text.secondary', opacity: 0.7 }}>
              {config.unit}
            </Typography>
          )}
        </div>

        {/* Progress Bar */}
        <div className="w-full mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>{config.minValue ?? config.min_value ?? 0}</span>
            <span>{config.maxValue ?? config.max_value ?? 100}</span>
          </div>
          <LinearProgress
            variant="determinate"
            value={getProgressPercentage(value)}
            sx={{ 
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: getProgressColor(value) === 'red' ? '#f44336' : 
                                getProgressColor(value) === 'yellow' ? '#ff9800' : 
                                getProgressColor(value) === 'green' ? '#4caf50' : '#2196f3'
              }
            }}
          />
        </div>

        {/* Status */}
        <div className="text-center">
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600,
              color: getProgressColor(value) === 'red' ? '#f44336' :
                     getProgressColor(value) === 'yellow' ? '#ff9800' : '#4caf50'
            }}
          >
            {getStatusText(value)}
          </Typography>
        </div>

        {/* Threshold Indicators */}
        {config.thresholds && Array.isArray(config.thresholds) && config.thresholds.length > 0 && (
          <div className="mt-4 w-full">
            <div className="flex justify-between text-xs text-gray-500 flex-wrap gap-2">
              {config.thresholds.map((threshold, index) => (
                <div key={index} className="flex items-center gap-1">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: threshold.color }}
                  ></div>
                  <span>{threshold.label || `${translate('resources.dashboards.widgets.gauge.status.threshold')} ${index + 1}`}: ≥ {threshold.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </BaseWidget>
  );
};

export default GaugeWidget;